import { normalizeLegacyStableIds } from '@/adapters/legacy/stableIds'
import { assertContract, validateSkin } from '@/core/release/contracts'
import { fetchArtifact, fetchVerifiedJson, VerifiedFetchError } from '@/core/release/verifiedFetch'
import { RuntimeCache, type CachedRuntimeBundle } from '@/core/release/runtimeCache'
import { audienceManifestUrl } from '@/core/runtime/environment'
import type { LegacyAudienceConfig } from '@/domain/audience/types'
import type { AudienceCapabilities, AudienceManifestV1, AudienceReleaseSkinPolicyV1, ResolvedAudienceSkinV1, RuntimeSource } from '@/domain/release/types'
import { createLegacyCompatibilitySkin } from '@/shared/theme/fixtures'

interface LegacyManifest {
  eventId?: string | number
  versionNo?: string | number
  configUrl?: string
  checksum?: string
  publishTime?: string
  eventEndTime?: string
  skinPolicy?: AudienceReleaseSkinPolicyV1
}

export type RuntimeLoadResult =
  | { state: 'ready-online' | 'ready-cached'; bundle: CachedRuntimeBundle }
  | { state: 'missing-stadium' | 'unpublished' | 'event-ended' | 'revoked' | 'invalid-release' | 'offline-no-cache'; reason: string }

export class AudienceRuntimeLoader {
  constructor(private readonly cache = new RuntimeCache(), private readonly fetcher: typeof fetch = fetch, private readonly now = () => new Date()) {}

  async load(stadiumId: string): Promise<RuntimeLoadResult> {
    if (!/^\d{16,20}$/.test(stadiumId)) return { state: 'missing-stadium', reason: '场馆参数无效' }
    let manifestValue: unknown
    try {
      manifestValue = await fetchVerifiedJson(
        audienceManifestUrl(stadiumId),
        { cache: 'no-cache' },
        this.fetcher,
      )
    } catch (error) {
      if (error instanceof VerifiedFetchError && error.code === 'HTTP' && error.status === 404) {
        await this.cache.putManifest({
          schemaVersion: 'venue-audience-manifest/v1', stadiumId, state: 'unpublished', eventId: null,
          audienceReleaseUrl: null, audienceReleaseChecksum: null, updatedAt: this.now().toISOString(),
        }).catch(() => undefined)
        return { state: 'unpublished', reason: '当前活动尚未发布' }
      }
      return this.cachedFallback(stadiumId, error)
    }
    return this.loadShared(stadiumId, manifestValue)
  }

  private async loadShared(stadiumId: string, value: unknown): Promise<RuntimeLoadResult> {
    if (!isRecord(value)) return { state: 'invalid-release', reason: '共享 Manifest 格式无效' }
    const manifest = value as LegacyManifest
    const eventId = String(manifest.eventId || '').trim()
    const configUrl = String(manifest.configUrl || '').trim()
    const checksum = String(manifest.checksum || '').trim()
    if (!eventId || !configUrl || !/^[a-f0-9]{64}$/i.test(checksum)) return { state: 'invalid-release', reason: '共享发布清单不完整或缺少有效校验和' }
    const eventEndTime = Date.parse(String(manifest.eventEndTime || ''))
    if (Number.isFinite(eventEndTime) && eventEndTime <= this.now().getTime()) return { state: 'event-ended', reason: '活动已经结束' }
    await this.cache.putManifest({
      schemaVersion: 'venue-audience-manifest/v1', stadiumId, state: 'active', eventId,
      audienceReleaseUrl: null, audienceReleaseChecksum: null,
      updatedAt: String(manifest.publishTime || this.now().toISOString()),
    }).catch(() => undefined)
    try {
      const configValue = await fetchVerifiedJson(configUrl, { sha256: checksum, cache: 'force-cache' }, this.fetcher)
      if (!isLegacyConfig(configValue)) throw new Error('共享活动内容格式无效')
      const config = await normalizeLegacyStableIds(configValue)
      const sharedSkins = await loadSharedManifestSkins(manifest.skinPolicy, this.fetcher)
      const skin = sharedSkins?.skins[sharedSkins.defaultSkinId] || createLegacyCompatibilitySkin()
      const validUntil = legacyValidUntil(manifest, this.now())
      const versionNo = String(manifest.versionNo || 'unknown')
      const bundle = createBundle(stadiumId, eventId, `shared-v${versionNo}`, 'shared-content', validUntil, config, skin, checksum, { broadcast: true, panorama: true }, this.now(), sharedSkins, versionNo)
      await this.cache.put(bundle).catch(() => undefined)
      return { state: 'ready-online', bundle }
    } catch (error) {
      const cached = await this.cache.get(stadiumId, eventId).catch(() => null)
      if (eligible(cached, this.now())) return { state: 'ready-cached', bundle: markCached(cached) }
      return { state: 'offline-no-cache', reason: message(error) }
    }
  }

  private async cachedFallback(stadiumId: string, error: unknown): Promise<RuntimeLoadResult> {
    const manifest = await this.cache.getManifest(stadiumId).catch(() => null)
    if (manifest && manifest.state !== 'active') return terminal(manifest.state)
    const cached = manifest?.state === 'active' && manifest.eventId
      ? await this.cache.get(stadiumId, manifest.eventId).catch(() => null)
      : null
    if (eligible(cached, this.now())) return { state: 'ready-cached', bundle: markCached(cached) }
    const reason = error instanceof VerifiedFetchError ? error.message : '网络不可用且没有有效缓存'
    return { state: 'offline-no-cache', reason }
  }
}

interface LoadedReleaseSkins {
  skins: Record<string, ResolvedAudienceSkinV1>
  skinOptions: Array<{ skinId: string; name: string }>
  defaultSkinId: string
}

function createBundle(stadiumId: string, eventId: string, releaseId: string, source: RuntimeSource, offlineValidUntil: string, config: LegacyAudienceConfig, skin: ResolvedAudienceSkinV1, checksum: string, capabilities: AudienceCapabilities, verifiedAt: Date, releaseSkins?: LoadedReleaseSkins, versionNo?: string): CachedRuntimeBundle {
  const allowedAssetUrls = collectAssetUrls(config)
  config._runtime = { stadiumId, eventId, releaseId, source, checksum, versionNo, allowedAssetUrls, capabilities }
  return { key: `${stadiumId}::${eventId}`, stadiumId, eventId, releaseId, source, verifiedAt: verifiedAt.toISOString(), offlineValidUntil, config, skin, capabilities, ...releaseSkins }
}

async function loadReleaseSkins(policy: AudienceReleaseSkinPolicyV1 | undefined, defaultSkin: ResolvedAudienceSkinV1, fetcher: typeof fetch): Promise<LoadedReleaseSkins | undefined> {
  if (!policy) return undefined
  const defaultOption = policy.allowedSkins.find((item) => item.skinId === policy.defaultSkinId)
  if (!defaultOption || defaultOption.resolvedSkinId !== defaultSkin.resolvedSkinId) throw new Error('活动默认皮肤策略与默认 artifact 不一致')
  const skins: Record<string, ResolvedAudienceSkinV1> = { [policy.defaultSkinId]: defaultSkin }
  await Promise.all(policy.allowedSkins.map(async (option) => {
    if (option.skinId === policy.defaultSkinId) return
    try {
      const value = assertContract(validateSkin, await fetchArtifact(option.artifact, fetcher), `ResolvedAudienceSkinV1(${option.skinId})`)
      if (value.resolvedSkinId !== option.resolvedSkinId) return
      skins[option.skinId] = value
    } catch {
      // Non-default skins are optional. A failed artifact is omitted so the
      // activity remains usable and the store deterministically falls back.
    }
  }))
  return {
    skins,
    defaultSkinId: policy.defaultSkinId,
    skinOptions: policy.allowedSkins.filter((option) => skins[option.skinId]).map(({ skinId, name }) => ({ skinId, name })),
  }
}

async function loadSharedManifestSkins(policy: AudienceReleaseSkinPolicyV1 | undefined, fetcher: typeof fetch): Promise<LoadedReleaseSkins | undefined> {
  if (!policy) return undefined
  const ids = new Set<string>()
  if (!policy.defaultSkinId || !Array.isArray(policy.allowedSkins) || policy.allowedSkins.length < 1 || policy.allowedSkins.length > 24) {
    throw new Error('活动皮肤策略无效')
  }
  for (const option of policy.allowedSkins) {
    if (!option.skinId || !option.name || !option.resolvedSkinId || ids.has(option.skinId)) throw new Error('活动皮肤选项无效')
    if (!option.artifact || typeof option.artifact.url !== 'string' || !/^[a-f0-9]{64}$/i.test(option.artifact.sha256) || !Number.isInteger(option.artifact.bytes) || option.artifact.bytes < 1) {
      throw new Error('活动皮肤产物描述无效')
    }
    ids.add(option.skinId)
  }
  const defaultOption = policy.allowedSkins.find((item) => item.skinId === policy.defaultSkinId)
  if (!defaultOption) throw new Error('活动默认皮肤不在允许列表中')
  const defaultSkin = assertContract(validateSkin, await fetchArtifact(defaultOption.artifact, fetcher), 'ResolvedAudienceSkinV1(default)')
  if (defaultSkin.resolvedSkinId !== defaultOption.resolvedSkinId) throw new Error('活动默认皮肤版本不一致')
  return loadReleaseSkins(policy, defaultSkin, fetcher)
}

function collectAssetUrls(value: unknown, result = new Set<string>()): string[] {
  if (Array.isArray(value)) for (const item of value) collectAssetUrls(item, result)
  else if (isRecord(value)) {
    if ('hdUrl' in value || 'originalUrl' in value || 'assetId' in value) {
      for (const key of ['hdUrl', 'originalUrl', 'url']) {
        const url = value[key]
        if (typeof url === 'string' && /^https:\/\//i.test(url)) result.add(url)
      }
    }
    for (const item of Object.values(value)) collectAssetUrls(item, result)
  }
  return [...result]
}

function legacyValidUntil(manifest: LegacyManifest, now: Date): string {
  const end = Date.parse(String(manifest.eventEndTime || ''))
  return new Date(Number.isFinite(end) && end > now.getTime() ? end : now.getTime() + 24 * 60 * 60 * 1000).toISOString()
}

function eligible(bundle: CachedRuntimeBundle | null, now: Date): bundle is CachedRuntimeBundle {
  return Boolean(bundle && Date.parse(bundle.offlineValidUntil) >= now.getTime() && bundle.config?._runtime?.eventId === bundle.eventId)
}
function markCached(bundle: CachedRuntimeBundle): CachedRuntimeBundle {
  const source: RuntimeSource = bundle.source === 'shared-content' || bundle.source === 'shared-cache'
    ? 'shared-cache'
    : 'legacy-cache'
  const capabilities = bundle.capabilities || bundle.config?._runtime?.capabilities || { broadcast: false, panorama: false }
  return { ...bundle, source, capabilities, config: { ...bundle.config, _runtime: { ...bundle.config._runtime, eventId: bundle.eventId, source, capabilities } } }
}
function terminal(state: AudienceManifestV1['state']): RuntimeLoadResult { return state === 'active' ? { state: 'invalid-release', reason: '活动发布状态无效' } : { state: state === 'ended' ? 'event-ended' : state, reason: `活动状态：${state}` } }
function message(error: unknown): string { return error instanceof Error ? error.message : '活动加载失败' }
function isLegacyConfig(value: unknown): value is LegacyAudienceConfig { return isRecord(value) && typeof value.projectName === 'string' }
function isRecord(value: unknown): value is Record<string, unknown> { return typeof value === 'object' && value !== null && !Array.isArray(value) }
